// Sistema de i18n minimalista (Sprint 8).
//
// - Sin libs externas (no react-i18next / lingui).
// - 2 idiomas: 'es' (default) y 'en'.
// - Detección: localStorage → navigator.language → 'es'.
// - Persistencia en localStorage.
// - API: `useT()` retorna función `(key, vars?) => string`.
// - Interpolación: `{name}` se reemplaza con `vars.name`.
//
// Uso:
//   const t = useT();
//   <h1>{t('game.title')}</h1>
//   <p>{t('game.wins', { count: 12 })}</p>

import { useEffect, useState } from 'react';

export type Locale = 'es' | 'en';

const STORAGE_KEY = 'regicide:locale';

const MESSAGES: Record<Locale, Record<string, string>> = {
  es: {
    'app.name': 'Regicide',
    'app.tagline': 'Derrota a la corrompida monarquía.',
    'home.cta.play': 'Jugar',
    'home.cta.howto': 'Cómo se juega',
    'home.cta.saves': 'Saves',
    'home.cta.leaderboard': 'Leaderboard',
    'home.card.cards': '🃏 54 cartas',
    'home.card.cards.desc': 'Mazo estándar + 2 Jokers',
    'home.card.players': '👥 1 jugador',
    'home.card.players.desc': 'Modo single-player contra IA',
    'home.card.enemies': '🎯 12 enemigos',
    'home.card.enemies.desc': 'Jacks, Queens y Kings',

    'play.start': 'Empezar partida',
    'play.intro': 'Derrota a la corrompida monarquía. 12 enemigos, 3 vidas, 54 cartas.',
    'play.phase.player-turn': 'Tu turno',
    'play.phase.enemy-turn': 'Turno del enemigo',
    'play.phase.won': 'Victoria',
    'play.phase.lost': 'Derrota',
    'play.actions.undo': 'Deshacer',
    'play.actions.save': 'Guardar',
    'play.actions.saved': 'Guardado',
    'play.actions.restart': 'Reiniciar',
    'play.actions.pass': 'Pasar (recibir daño)',
    'play.drop.hint': 'Arrastrá una carta acá para jugar (ataque).',
    'play.stats.turn': 'Turno',
    'play.stats.hand': 'Mano',
    'play.stats.castle': 'Castillo',
    'play.stats.discard': 'Descarte',
    'play.stats.castleDiscard': 'Castle discard',
    'play.stats.seed': 'Seed',
    'play.combo.empty': 'Selecciona 1, 2 o 3 cartas para combinar.',
    'play.combo.valid': '✓ Válido ({count} carta|cartas)',
    'play.combo.invalid': '✗ {reason}',
    'play.combo.attack': 'Atacar',
    'play.combo.defend': 'Defender',
    'play.combo.clear': 'Limpiar',
    'play.modal.victory': '¡Victoria!',
    'play.modal.victory.body': 'Has derrotado a toda la corrompida monarquía en {turns} turnos.',
    'play.modal.victory.new': 'Nueva partida',
    'play.modal.victory.close': 'Cerrar',
    'play.modal.lost': 'Derrota',
    'play.modal.lost.body': 'Te quedaste sin vidas. La monarquía ha ganado… por ahora.',
    'play.modal.lost.retry': 'Reintentar',
    'play.modal.lost.close': 'Cerrar',
    'play.modal.restart': '¿Reiniciar la partida?',
    'play.modal.restart.body':
      'Vas a perder el progreso actual y la partida volverá a empezar con el mismo seed.',
    'play.modal.restart.confirm': 'Sí, reiniciar',
    'play.modal.restart.cancel': 'Cancelar',

    'saves.title': 'Partidas guardadas',
    'saves.empty': 'No tenés partidas guardadas todavía.',
    'saves.empty.hint': 'Empezá una partida y se guardará automáticamente cada 30s.',
    'saves.load': 'Cargar',
    'saves.delete': 'Borrar',
    'saves.modal.delete': '¿Borrar partida?',
    'saves.modal.delete.body': 'Vas a borrar {name}. Esta acción no se puede deshacer.',
    'saves.modal.delete.confirm': 'Sí, borrar',
    'saves.modal.delete.cancel': 'Cancelar',
    'saves.savedOn': 'Guardada: {date}',
    'saves.noPersistence':
      'Almacenamiento local no disponible. Tu navegador no soporta IndexedDB (modo privado o cookies deshabilitadas). Las partidas no se guardarán en este dispositivo.',
    'saves.column.seed': 'Semilla',
    'saves.column.lives': 'Vidas',
    'saves.column.turn': 'Turno',
    'saves.column.phase': 'Fase',
    'saves.phase.in_progress': 'En curso',
    'saves.phase.won': 'Victoria',
    'saves.phase.lost': 'Derrota',

    'leaderboard.title': 'Leaderboard',
    'leaderboard.sort.time': 'Tiempo',
    'leaderboard.sort.moves': 'Turnos',
    'leaderboard.sort.lives': 'Vidas',
    'leaderboard.empty': 'Todavía no hay partidas en el leaderboard.',
    'leaderboard.empty.hint': 'Sé el primero en terminar una partida.',
    'leaderboard.error':
      'No se pudo cargar el leaderboard. Si estás en local, asegurate de tener el backend corriendo con `docker compose up -d --build`.',
    'leaderboard.column.time': 'Tiempo',
    'leaderboard.column.moves': 'Turnos',
    'leaderboard.column.lives': 'Vidas',

    'howto.title': 'Cómo se juega',
    'howto.intro':
      'Regicide es un juego cooperativo de cartas. Los Jacks, Queens y Kings son los enemigos. Derriba los 12 para ganar.',
    'howto.turn.title': 'Turno típico',
    'howto.turn.1': 'Robar 1 carta del mazo de héroes.',
    'howto.turn.2': 'Jugar 1, 2 o 3 cartas combinadas (poder ≤ 10).',
    'howto.turn.3': 'Atacar al enemigo activo o defenderte.',
    'howto.turn.4': 'El enemigo ataca (poder: 10 / 15 / 20).',
    'howto.more': 'Ver {link} para las reglas completas.',
    'howto.more.link': 'GAME_RULES.md',

    'settings.title': 'Ajustes',
    'settings.language': 'Idioma',
    'settings.sound': 'Sonidos',
    'settings.sound.on': 'Activados',
    'settings.sound.off': 'Desactivados',

    'a11y.skip': 'Saltar al contenido principal',
    'a11y.loading': 'Cargando…',

    'common.back': '← Inicio',
    'common.close': 'Cerrar',
    'common.cancel': 'Cancelar',
  },
  en: {
    'app.name': 'Regicide',
    'app.tagline': 'Defeat the corrupted monarchy.',
    'home.cta.play': 'Play',
    'home.cta.howto': 'How to play',
    'home.cta.saves': 'Saves',
    'home.cta.leaderboard': 'Leaderboard',
    'home.card.cards': '🃏 54 cards',
    'home.card.cards.desc': 'Standard deck + 2 Jokers',
    'home.card.players': '👥 1 player',
    'home.card.players.desc': 'Single-player vs IA',
    'home.card.enemies': '🎯 12 enemies',
    'home.card.enemies.desc': 'Jacks, Queens and Kings',

    'play.start': 'Start game',
    'play.intro': 'Defeat the corrupted monarchy. 12 enemies, 3 lives, 54 cards.',
    'play.phase.player-turn': 'Your turn',
    'play.phase.enemy-turn': "Enemy's turn",
    'play.phase.won': 'Victory',
    'play.phase.lost': 'Defeat',
    'play.actions.undo': 'Undo',
    'play.actions.save': 'Save',
    'play.actions.saved': 'Saved',
    'play.actions.restart': 'Restart',
    'play.actions.pass': 'Pass (take damage)',
    'play.drop.hint': 'Drag a card here to play (attack).',
    'play.stats.turn': 'Turn',
    'play.stats.hand': 'Hand',
    'play.stats.castle': 'Castle',
    'play.stats.discard': 'Discard',
    'play.stats.castleDiscard': 'Castle discard',
    'play.stats.seed': 'Seed',
    'play.combo.empty': 'Select 1, 2 or 3 cards to combine.',
    'play.combo.valid': '✓ Valid ({count} card|cards)',
    'play.combo.invalid': '✗ {reason}',
    'play.combo.attack': 'Attack',
    'play.combo.defend': 'Defend',
    'play.combo.clear': 'Clear',
    'play.modal.victory': 'Victory!',
    'play.modal.victory.body': 'You have defeated the entire corrupted monarchy in {turns} turns.',
    'play.modal.victory.new': 'New game',
    'play.modal.victory.close': 'Close',
    'play.modal.lost': 'Defeat',
    'play.modal.lost.body': 'You ran out of lives. The monarchy has won… for now.',
    'play.modal.lost.retry': 'Retry',
    'play.modal.lost.close': 'Close',
    'play.modal.restart': 'Restart the game?',
    'play.modal.restart.body':
      'You will lose current progress and the game will restart with the same seed.',
    'play.modal.restart.confirm': 'Yes, restart',
    'play.modal.restart.cancel': 'Cancel',

    'saves.title': 'Saved games',
    'saves.empty': 'You have no saved games yet.',
    'saves.empty.hint': 'Start a game and it will be auto-saved every 30 seconds.',
    'saves.load': 'Load',
    'saves.delete': 'Delete',
    'saves.modal.delete': 'Delete save?',
    'saves.modal.delete.body': 'You are about to delete {name}. This action cannot be undone.',
    'saves.modal.delete.confirm': 'Yes, delete',
    'saves.modal.delete.cancel': 'Cancel',
    'saves.savedOn': 'Saved: {date}',
    'saves.noPersistence':
      'Local storage is not available. Your browser does not support IndexedDB (private mode or cookies disabled). Games will not be saved on this device.',
    'saves.column.seed': 'Seed',
    'saves.column.lives': 'Lives',
    'saves.column.turn': 'Turn',
    'saves.column.phase': 'Phase',
    'saves.phase.in_progress': 'In progress',
    'saves.phase.won': 'Won',
    'saves.phase.lost': 'Lost',

    'leaderboard.title': 'Leaderboard',
    'leaderboard.sort.time': 'Time',
    'leaderboard.sort.moves': 'Moves',
    'leaderboard.sort.lives': 'Lives',
    'leaderboard.empty': 'No games on the leaderboard yet.',
    'leaderboard.empty.hint': 'Be the first to finish a game.',
    'leaderboard.error':
      'Could not load the leaderboard. If you are running locally, make sure the backend is up with `docker compose up -d --build`.',
    'leaderboard.column.time': 'Time',
    'leaderboard.column.moves': 'Moves',
    'leaderboard.column.lives': 'Lives',

    'howto.title': 'How to play',
    'howto.intro':
      'Regicide is a cooperative card game. Jacks, Queens and Kings are the enemies. Beat all 12 to win.',
    'howto.turn.title': 'Typical turn',
    'howto.turn.1': 'Draw 1 card from the heroes deck.',
    'howto.turn.2': 'Play 1, 2 or 3 combined cards (power ≤ 10).',
    'howto.turn.3': 'Attack the active enemy or defend.',
    'howto.turn.4': 'The enemy attacks (power: 10 / 15 / 20).',
    'howto.more': 'See {link} for the full rules.',
    'howto.more.link': 'GAME_RULES.md',

    'settings.title': 'Settings',
    'settings.language': 'Language',
    'settings.sound': 'Sound',
    'settings.sound.on': 'On',
    'settings.sound.off': 'Off',

    'a11y.skip': 'Skip to main content',
    'a11y.loading': 'Loading…',

    'common.back': '← Home',
    'common.close': 'Close',
    'common.cancel': 'Cancel',
  },
};

function detectInitialLocale(): Locale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  }
  if (typeof navigator !== 'undefined') {
    const lang = navigator.language.toLowerCase().split('-')[0];
    if (lang === 'en') return 'en';
  }
  return 'es';
}

let currentLocale: Locale = 'es';
const subscribers = new Set<() => void>();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(loc: Locale): void {
  currentLocale = loc;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, loc);
  }
  for (const cb of subscribers) cb();
}

export function subscribeLocale(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function interpolate(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const v = vars[key];
    return v === undefined ? `{${key}}` : String(v);
  });
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const table = MESSAGES[currentLocale];
  const raw = table[key] ?? MESSAGES.es[key] ?? key;
  return vars ? interpolate(raw, vars) : raw;
}

/** Hook React: devuelve función `t` ligada al locale actual + re-render en cambio. */
export function useT(): (key: string, vars?: Record<string, string | number>) => string {
  const [, setN] = useState(0);
  useEffect(() => {
    // Hidratar locale desde storage en cliente.
    currentLocale = detectInitialLocale();
    setN((n) => n + 1);
    return subscribeLocale(() => setN((n) => n + 1));
  }, []);
  return t;
}

/** Componente simple para inyectar el locale inicial en cliente. */
export function LocaleProvider(): null {
  useEffect(() => {
    currentLocale = detectInitialLocale();
    for (const cb of subscribers) cb();
  }, []);
  return null;
}
